import lodash from 'lodash';

import { queryGraphQL, mutateGraphQL } from 'common/util/query';
import { toQuery, toMutation, node, nodeWithArgs, aliasNodeWithArgs } from 'common/util/queryHelper';
import { addApplicationMessage, addApplicationMessages } from 'common/actions/applicationMessagesActions';
import {
    buildGetCustomizeColumnsQuery,
    buildSaveUserReportCustomizeColumnsQuery,
    getReportDefaultColumns,
    reportOptionData,
} from '../utils/reportUtils';
import {
    reportLinks,
    binderStatus,
    REPORT_INSTITUTION_LOAD_DATA_SOURCE,
    REPORT_INSTITUTION_LOAD_SELECTED_REPORT,
    REPORT_INSTITUTION_SELECT_CHOICE_OPTION,
    REPORT_INSTITUTION_SET_REPORT_DATA,
    REPORT_INSTITUTION_UNLOAD,
    REPORT_TABLE_ACTIONS,
    statusKey,
    reportTypes,
} from '../utils/constants';

export const loadDataSource = (dataSource, parameters) => {
    return async dispatch => {
        if (dataSource === statusKey) {
            return dispatch(setDataSource(dataSource, binderStatus, false));
        }

        const handler = dataSourceHandlers[dataSource];
        if (!handler) {
            return;
        }

        try {
            dispatch(setDataSource(dataSource, [], true));
            const data = await handler(parameters);
            dispatch(setDataSource(dataSource, data, false));
        } catch (err) {
            dispatch(addApplicationMessage('error', `An error occurred when loading a data source: ${dataSource}`));
            dispatch(setDataSource, null, false);
        }
    };
};

const dataSourceHandlers = {
    binders: loadBinders,
    documentTypes: loadDocumentTypes,
    emails: loadEmails,
};

async function loadBinders({ institutionId }) {
    const queryNode = aliasNodeWithArgs('loadBinders', 'bindersByInstitution', { institutionId, includeStudyBinders: true }, [
        'key: id',
        'label: title',
    ]);
    const response = await queryGraphQL(toQuery(queryNode));
    return lodash.get(response.data, 'loadBinders', []);
}

async function loadDocumentTypes({ institutionId }) {
    const queryNode = aliasNodeWithArgs('loadDocumentTypes', 'documentTypes', { institutionId }, [
        'key: id',
        'label: displayName',
    ]);
    const response = await queryGraphQL(toQuery(queryNode));
    return lodash.get(response.data, 'loadDocumentTypes', []);
}

async function loadEmails({ institutionId }) {
    const queryNode = nodeWithArgs('getEmailsForAuditTrailReport', {
        institutionId,
    });
    const response = await queryGraphQL(toQuery(queryNode));
    return lodash.get(response.data, 'getEmailsForAuditTrailReport', []);
}

export function loadSelectedReport(name) {
    return async dispatch => {
        const report = getReportByName(name);
        dispatch(setSelectedReport(report));
    };
}

export function getReportByName(name) {
    return lodash.find(reportOptionData, { name }) || {};
}

export function submitReport(submitData) {
    return async dispatch => {
        const { reportType } = submitData;
        const queryNode = nodeWithArgs('getReportAPIComplionAdmin', submitData, ['reportURL', 'description', 'willBeSentViaEmail']);
        const response = await queryGraphQL(toQuery(queryNode));
        if ([reportTypes.userDocumentAuditTrail, reportTypes.task, reportTypes.document, reportTypes.billableHistory].includes(reportType)) {
            return dispatch(addApplicationMessage('success', 'Report will be sent to your email shortly.'));
        }
        dispatch(
            addApplicationMessage('download', lodash.get(response.data.getReportAPIComplionAdmin, 'reportURL', '')),
        );
    };
}

function setDataSource(dataSource, data, loading) {
    return {
        type: REPORT_INSTITUTION_LOAD_DATA_SOURCE,
        payload: {
            dataSource,
            data,
            loading,
        },
    };
}

export const setReportValue = (dataKey, value) => {
    return {
        type: REPORT_INSTITUTION_SET_REPORT_DATA,
        payload: {
            dataKey,
            value,
        },
    };
};

export const selectChoice = (choice, id) => {
    return {
        type: REPORT_INSTITUTION_SELECT_CHOICE_OPTION,
        payload: {
            choice,
            id,
        },
    };
};

export const setSelectedReport = report => {
    return {
        type: REPORT_INSTITUTION_LOAD_SELECTED_REPORT,
        payload: report,
    };
};

export const resetSelectedReport = report => {
    return {
        type: REPORT_INSTITUTION_UNLOAD,
        payload: report,
    };
};

export function fetchReportData(queryNode, reportType) {
    return async dispatch => {
        dispatch(loadingReportData(reportType));
        dispatch(resetReportData(reportType));

        try {
            const results = await queryGraphQL(toQuery(queryNode));
            if (results.errors) {
                const reportTypeObj = lodash.find(reportLinks, { type: reportType }) || { title: reportType };
                dispatch(addApplicationMessage('error', `Could not load report data for ${reportTypeObj.title}`));
            } else {
                const responseData = results.data[queryNode.name];
                dispatch(setReportData(responseData, reportType));
            }

            dispatch(loadedReportData(reportType));
        } catch (error) {
            dispatch(addApplicationMessage('error', error.responseText));
        }
    };
}

export function fetchCustomizeColumns(reportType, institutionId) {
    return async dispatch => {
        dispatch(loadingCustomizeColumns(reportType));
        const queryNode = buildGetCustomizeColumnsQuery(reportType, institutionId);
        try {
            const response = await queryGraphQL(toQuery(queryNode));
            const { getCustomizeColumns } = response.data;
            const customizeColumns = !lodash.isEmpty(getCustomizeColumns)
                ? getCustomizeColumns.columnNames
                : getReportDefaultColumns(reportType);
            dispatch(setCustomizeColumnsData({ reportType, data: customizeColumns }));
        } catch (error) {
            dispatch(addApplicationMessage('error', error.responseText));
        } finally {
            dispatch(loadedCustomizeColumns(reportType));
        }
    };
}

export function setCustomizeColumns(reportType, columnNames, institutionId) {
    return async dispatch => {
        dispatch(loadingCustomizeColumns(reportType));
        const queryNode = buildSaveUserReportCustomizeColumnsQuery(reportType, columnNames, institutionId);
        try {
            const response = await mutateGraphQL(toMutation(queryNode));
            const { saveUserReportCustomizeColumns } = response.data;
            const customizeColumns = lodash.get(
                saveUserReportCustomizeColumns,
                'columnNames',
                getReportDefaultColumns(reportType),
            );
            dispatch(setCustomizeColumnsData({ reportType, data: customizeColumns }));
        } catch (error) {
            dispatch(addApplicationMessage('error', error.responseText));
        } finally {
            dispatch(loadedCustomizeColumns(reportType));
        }
    };
}

export const getEmailConfigQueryNode = (reportType, institutionId) => {
    return nodeWithArgs(
        'getEmailConfigurations',
        {
            reportType,
            institutionId,
        },
        [
            'id',
            'institutionId',
            'reportType',
            'groups',
            'selectStart',
            'frequency',
            node('user', ['id', 'sub', 'emailAddress', 'displayName', 'firstName', 'lastName']),
        ],
    );
};

export function fetchEmailConfigs(reportType, institutionId) {
    return async (dispatch, getState) => {
        dispatch(loadingEmailConfig(reportType));
        dispatch(resetEmailConfigs(reportType));

        try {
            const queryNode = getEmailConfigQueryNode(reportType, institutionId);
            const results = await queryGraphQL(toQuery(queryNode));
            if (results.errors) {
                const reportTypeObj = lodash.find(reportLinks, { type: reportType }) || { title: reportType };
                dispatch(
                    addApplicationMessage('error', `Could not load email configuration for ${reportTypeObj.title}`),
                );
            } else {
                const resonseData = results.data[queryNode.name];
                dispatch(setEmailConfig(resonseData, reportType));
            }
        } catch (error) {
            dispatch(addApplicationMessage('error', error.responseText));
        }
        dispatch(loadedEmailConfig(reportType));
    };
}

export function removeUsersFromEmailList(ids, institutionId) {
    return async dispatch => {
        try {
            const queryNode = nodeWithArgs('deleteUserReportEmailConfigurationByIds', { institutionId, input: { ids } }, null);
            await mutateGraphQL(toMutation(queryNode));
        } catch (error) {
            dispatch(addApplicationMessage('error', error.responseText));
        }
    };
}

export function setReportData(data, reportType) {
    return {
        type: REPORT_TABLE_ACTIONS.SET_REPORT_DATA,
        payload: {
            reportType,
            data,
        },
    };
}

export function resetReportData(reportType) {
    return {
        type: REPORT_TABLE_ACTIONS.RESET_REPORT_DATA,
        payload: {
            reportType,
        },
    };
}

export function loadingReportData(reportType) {
    return { type: REPORT_TABLE_ACTIONS.LOADING_REPORT_DATA, payload: { reportType } };
}

export function loadedReportData(reportType) {
    return { type: REPORT_TABLE_ACTIONS.LOADED_REPORT_DATA, payload: { reportType } };
}

export function loadingCustomizeColumns(reportType) {
    return { type: REPORT_TABLE_ACTIONS.LOADING_CUSTOMIZE_COLUMNS, payload: { reportType } };
}

export function loadedCustomizeColumns(reportType) {
    return { type: REPORT_TABLE_ACTIONS.LOADED_CUSTOMIZE_COLUMNS, payload: { reportType } };
}

export function setCustomizeColumnsData(data) {
    return { type: REPORT_TABLE_ACTIONS.SET_CUSTOMIZE_COLUMNS_DATA, payload: data };
}

export function resetCustomizeColumns(reportType) {
    return { type: REPORT_TABLE_ACTIONS.RESET_CUSTOMIZE_COLUMNS, payload: { reportType } };
}

export function loadingEmailConfig(reportType) {
    return { type: REPORT_TABLE_ACTIONS.LOADING_EMAIL_CONFIG, payload: { reportType } };
}

export function loadedEmailConfig(reportType) {
    return { type: REPORT_TABLE_ACTIONS.LOADED_EMAIL_CONFIG, payload: { reportType } };
}

export function loadingAddEmailConfig(reportType) {
    return { type: REPORT_TABLE_ACTIONS.LOADING_ADD_EMAIL_CONFIG, payload: { reportType } };
}

export function loadedAddEmailConfig(reportType) {
    return { type: REPORT_TABLE_ACTIONS.LOADED_ADD_EMAIL_CONFIG, payload: { reportType } };
}

export function setEmailConfig(data, reportType) {
    return {
        type: REPORT_TABLE_ACTIONS.SET_EMAIL_CONFIGS,
        payload: {
            reportType,
            data,
        },
    };
}

export function resetEmailConfigs(reportType) {
    return {
        type: REPORT_TABLE_ACTIONS.RESET_EMAIL_CONFIGS,
        payload: {
            reportType,
        },
    };
}

/*
Download Report

dispatches addApplicationMessage with type download, 
ttl of zero ensures the toast does not disappear

Returns:
    null
*/
export function downloadReport(reportId, institutionId, reportName) {
    return async dispatch => {
        try {
            const query = toQuery(nodeWithArgs('getReportPresignedUrl', {
                reportId,
                institutionId,
                reportName,
            }, [
                'reportURL',
            ]));
            const response = await queryGraphQL(query);
            if (response.formattedErrors) {
                dispatch(addApplicationMessages('error', response.formattedErrors));
            }
            else {
                dispatch(addApplicationMessage('download', response.data.getReportPresignedUrl.reportURL, { ttl: 0 }));
            }
        } catch (error) {
            dispatch(addApplicationMessage('error', error.responseText));
        }
    };
}
